import { Picker, type PickerItem } from "@/components/ui/Picker";
import { Loader2, UserPlus, X } from "lucide-react";

import { BADGE_VARIANT_CLASSES } from "./constants";
import type { UserBadgeVariant, UserOption } from "./types";

export interface UserPickerBadgesViewProps {
  /** Pre-sorted (alphabetical by name) by the caller — this view never re-sorts. */
  assigned: UserOption[];
  candidates: UserOption[];
  variant: UserBadgeVariant;
  addLabel: string;
  searchPlaceholder?: string;
  emptyText?: string;
  isEditable: boolean;
  isLoading: boolean;
  onAdd: (userId: string) => void;
  onRemove: (userId: string) => void;
}

export const UserPickerBadgesView = ({
  assigned,
  candidates,
  variant,
  addLabel,
  searchPlaceholder,
  emptyText,
  isEditable,
  isLoading,
  onAdd,
  onRemove,
}: UserPickerBadgesViewProps) => {
  const { badge, hover, addButton } = BADGE_VARIANT_CLASSES[variant];
  const items: PickerItem[] = candidates.map((user) => ({
    value: user.user_id,
    label: user.name,
  }));

  return (
    <div className={`flex flex-col gap-1 ${!isEditable ? "cursor-not-allowed" : ""}`}>
      {assigned.map((user) => (
        <span
          key={user.user_id}
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-normal ${badge} w-fit`}
        >
          {user.name}
          {isEditable && (
            <button
              type="button"
              aria-label={`Remove ${user.name}`}
              className={`ml-1.5 flex items-center ${hover}`}
              onClick={(e) => {
                e.stopPropagation();
                onRemove(user.user_id);
              }}
            >
              <X className="h-3 w-3 cursor-pointer" />
            </button>
          )}
        </span>
      ))}

      {isEditable && (
        <Picker
          items={items}
          value={null}
          onChange={onAdd}
          sortItems
          isLoading={isLoading}
          searchPlaceholder={searchPlaceholder}
          commandEmptyLabel={emptyText}
          contentClassName="w-64"
          popoverModal
          trigger={
            <button
              type="button"
              aria-label={addLabel}
              disabled={isLoading}
              className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-normal ${addButton} disabled:cursor-not-allowed disabled:opacity-50`}
            >
              {isLoading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <UserPlus className="h-3 w-3" />
              )}
            </button>
          }
        />
      )}
    </div>
  );
};
